/**
 * AstorChatFlyover — Shared Gemini-style floating chat panel.
 *
 * This is the shared design-system component. Each portal provides a thin
 * wrapper that injects portal-specific props (agentId, suggestions, auth, etc.).
 *
 * Features:
 * - Side/Wide size toggle (like Gemini)
 * - No blocking backdrop — user can interact with the page behind
 * - Animated gradient greeting text
 * - Suggestion chips at bottom when chat is empty
 * - Slide-up enter animation
 */

import DropdownIcon from '@mui/icons-material/ArrowDropDown';
import AstorIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/DeleteOutlined';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import ReportIcon from '@mui/icons-material/ReportOutlined';
import SendIcon from '@mui/icons-material/Send';
import WideIcon from '@mui/icons-material/ViewModule';
import SideIcon from '@mui/icons-material/ViewSidebar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../hooks';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { AstorThread, type AstorThreadAdapter } from '../AstorThread';
import { GlassIconButton } from '../GlassIconButton/GlassIconButton';
import { ShimmerLoader } from '../ShimmerLoader/ShimmerLoader';
import type { ChatMessage, ChatSource } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Panel size presets */
type PanelSize = 'side' | 'wide';

const PANEL_WIDTHS: Record<PanelSize, number> = {
  side: 380,
  wide: 520,
};

/** A suggestion item shown in the empty state and the main menu. */
export interface AstorSuggestion {
  key: string;
  icon: string;
  i18nKey: string;
}

export interface AstorChatFlyoverProps {
  /** Whether the flyover is visible. */
  open: boolean;
  /** Close handler. */
  onClose: () => void;

  // --- Chat state (injected by portal wrapper) ---
  /** Chat messages to display. */
  messages: ChatMessage[];
  /** Whether the assistant is currently streaming a response. */
  isStreaming: boolean;
  /** Error message to display, if any. */
  error?: string | null;
  /** Send a message to the agent. */
  onSendMessage: (agentId: string, text: string) => void | Promise<void>;
  /** Clear the chat history. */
  onClearMessages: () => void;

  // --- Portal-specific configuration ---
  /** The Astor agent ID for this portal (e.g. 'admin-assistant'). */
  agentId: string;
  /** The user's display name for the greeting. */
  userName?: string;
  /** i18n key for the hint text below the greeting. */
  hintKey: string;
  /** Suggestion items shown in the empty state and menu. */
  suggestions: AstorSuggestion[];

  /** Render message content (typically wraps ReactMarkdown). Falls back to plain text. */
  renderContent?: (content: string) => ReactNode;
  /** Optional custom renderer for source badges in messages. */
  renderSource?: (source: ChatSource, index: number) => ReactNode;
  /**
   * Called when an inline tool renderer (e.g. CasesTable row click) requests
   * navigation. Wire to `router.push` from the host portal. Issue #1432 Phase 2.
   */
  onToolNavigate?: (path: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AstorChatFlyover = ({
  open,
  onClose,
  messages,
  isStreaming,
  error,
  onSendMessage,
  onClearMessages,
  agentId,
  userName,
  hintKey,
  suggestions,
  renderContent,
  // renderSource is part of the public API for backwards compatibility but
  // unused now that AstorThread renders the bubble (Issue #1434).
  renderSource: _renderSource,
  onToolNavigate,
}: AstorChatFlyoverProps): ReactElement | null => {
  const { t } = useI18n();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [panelSize, setPanelSize] = useState<PanelSize>('side');
  const [sizeMenuAnchor, setSizeMenuAnchor] = useState<null | HTMLElement>(null);
  const [mainMenuAnchor, setMainMenuAnchor] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Issue #1434 — adapter that wires the flyover's existing message-pump props
  // into AstorThread's runtime. forkFromMessage / reloadFromMessage are
  // omitted intentionally: the flyover's public API doesn't carry those
  // callbacks, so assistant-ui suppresses the Edit / Reload action buttons
  // (the standalone /astor/chat page exposes them). Branch picker still
  // shows when hydrated messages have parentMessageId pointing at siblings.
  const threadAdapter = useMemo<AstorThreadAdapter>(
    () => ({
      messages: messages as AstorThreadAdapter['messages'],
      isStreaming,
      sendMessage: (text: string) => onSendMessage(agentId, text),
      cancelStream: onClearMessages,
      onToolNavigate,
    }),
    [messages, isStreaming, onSendMessage, agentId, onClearMessages, onToolNavigate],
  );

  // --- Handlers ---

  const handleSendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (trimmed && !isStreaming) {
      setInputValue('');
      await onSendMessage(agentId, trimmed);
    }
  }, [inputValue, isStreaming, onSendMessage, agentId]);

  const handleSuggestionClick = useCallback(
    async (text: string) => {
      if (!isStreaming) {
        await onSendMessage(agentId, text);
      }
    },
    [isStreaming, onSendMessage, agentId],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !isStreaming) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const handleSizeMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setSizeMenuAnchor(event.currentTarget);
  };

  const handleSizeMenuClose = (): void => {
    setSizeMenuAnchor(null);
  };

  const handleSizeChange = (size: PanelSize): void => {
    setPanelSize(size);
    handleSizeMenuClose();
  };

  const handleMainMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMainMenuAnchor(event.currentTarget);
  };

  const handleMainMenuClose = (): void => {
    setMainMenuAnchor(null);
  };

  const handleClearHistory = (): void => {
    onClearMessages();
    handleMainMenuClose();
  };

  const handleSendFeedback = (): void => {
    window.open('mailto:feedback@opsmatrix.ai?subject=Astor%20Feedback', '_blank');
    handleMainMenuClose();
  };

  const handleReport = (): void => {
    window.open('mailto:support@opsmatrix.ai?subject=Astor%20Issue%20Report', '_blank');
    handleMainMenuClose();
  };

  // --- Derived ---

  const panelWidth = PANEL_WIDTHS[panelSize];

  const greetingText = userName ? t('astorChat.greeting', { name: userName }) : t('astorChat.greetingGeneric');

  // TODO(#1758 F2): kept as blue (decorative AI branding, not nav/selection) — revisit when design confirms Astor brand palette.
  const gradientText = (
    <>
      <Typography
        variant="h5"
        sx={{
          background: isDark
            ? `linear-gradient(90deg, ${baseColors.info[400]} 0%, ${baseColors.primary[300]} 50%, ${baseColors.info[400]} 100%)`
            : `linear-gradient(90deg, ${baseColors.info[500]} 0%, ${baseColors.primary[400]} 50%, ${baseColors.info[500]} 100%)`,
          backgroundSize: '200% 100%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'astorGradientShift 3s ease infinite',
          fontWeight: 400,
          textAlign: 'center',
          '@keyframes astorGradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      >
        {greetingText}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
        {t(hintKey)}
      </Typography>
    </>
  );

  // --- Glass styling ---

  const glass = isDark ? glassEffects.dark : glassEffects.light;

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: panelWidth,
          height: '60vh',
          minHeight: 400,
          maxHeight: 700,
          zIndex: theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.default,
          boxShadow: glass.shadow,
          overflow: 'hidden',
          transition: 'width 200ms ease',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GlassIconButton size="small" onClick={handleMainMenuOpen} sx={{ color: 'text.secondary' }}>
              <MenuIcon fontSize="small" />
            </GlassIconButton>
            <Menu
              anchorEl={mainMenuAnchor}
              open={Boolean(mainMenuAnchor)}
              onClose={handleMainMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { mt: 0.5, minWidth: 200, boxShadow: glass.shadow },
                },
                backdrop: { invisible: true },
              }}
            >
              <MenuItem disabled sx={{ opacity: 0.7 }}>
                <ListItemText>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {t('astorChat.menu.moreSuggestions')}
                  </Typography>
                </ListItemText>
              </MenuItem>
              {suggestions.map((suggestion) => (
                <MenuItem
                  key={suggestion.key}
                  onClick={() => {
                    void handleSuggestionClick(t(suggestion.i18nKey));
                    handleMainMenuClose();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography sx={{ fontSize: iconSizes.sm }}>{suggestion.icon}</Typography>
                  </ListItemIcon>
                  <ListItemText>{t(suggestion.i18nKey)}</ListItemText>
                </MenuItem>
              ))}
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={handleClearHistory} disabled={messages.length === 0}>
                <ListItemIcon>
                  <ClearIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('astorChat.clearHistory')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleSendFeedback}>
                <ListItemIcon>
                  <FeedbackIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('astorChat.menu.sendFeedback')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleReport}>
                <ListItemIcon>
                  <ReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('astorChat.menu.report')}</ListItemText>
              </MenuItem>
            </Menu>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AstorIcon sx={{ fontSize: iconSizes.base, color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {t('astorChat.title')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GlassIconButton size="small" onClick={handleSizeMenuOpen} sx={{ color: 'text.secondary' }}>
              {panelSize === 'side' ? <SideIcon fontSize="small" /> : <WideIcon fontSize="small" />}
              <DropdownIcon fontSize="small" />
            </GlassIconButton>
            <Menu
              anchorEl={sizeMenuAnchor}
              open={Boolean(sizeMenuAnchor)}
              onClose={handleSizeMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: { mt: 0.5, minWidth: 150, boxShadow: glass.shadow },
                },
                backdrop: { invisible: true },
              }}
            >
              <MenuItem onClick={() => handleSizeChange('side')} selected={panelSize === 'side'}>
                <ListItemIcon>
                  <SideIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('astorChat.size.side')}</ListItemText>
                {panelSize === 'side' && <Typography sx={{ ml: 2 }}>&#10003;</Typography>}
              </MenuItem>
              <MenuItem onClick={() => handleSizeChange('wide')} selected={panelSize === 'wide'}>
                <ListItemIcon>
                  <WideIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('astorChat.size.wide')}</ListItemText>
                {panelSize === 'wide' && <Typography sx={{ ml: 2 }}>&#10003;</Typography>}
              </MenuItem>
            </Menu>

            <GlassIconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </GlassIconButton>
          </Box>
        </Box>

        {/* Content area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages or empty state */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: messages.length === 0 ? 'center' : 'flex-start',
              p: 2,
            }}
          >
            {messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>{gradientText}</Box>
            ) : (
              <AstorThread adapter={threadAdapter} showComposer={false} renderContent={renderContent} />
            )}
          </Box>

          {/* Error display */}
          {error != null && (
            <Box
              sx={{
                mx: 2,
                mb: 1,
                p: 1.5,
                backgroundColor: `${theme.palette.error.main}15`,
                borderRadius: 1,
                border: `1px solid ${theme.palette.error.main}30`,
              }}
            >
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Suggestions (at bottom when empty) */}
          {messages.length === 0 && (
            <Box sx={{ px: 2, pb: 2 }}>
              {suggestions.map((suggestion) => (
                <Box
                  key={suggestion.key}
                  onClick={() => void handleSuggestionClick(t(suggestion.i18nKey))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Typography sx={{ color: 'text.secondary', fontSize: iconSizes.md }}>{suggestion.icon}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {t(suggestion.i18nKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Input area (Gemini style) */}
          <Box sx={{ p: 2, pt: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                pl: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                '&:focus-within': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                },
              }}
            >
              <Box
                component="input"
                ref={inputRef}
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('astorChat.placeholder')}
                disabled={isStreaming}
                sx={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  fontSize: '0.9375rem', // body2 / 15pt Apple Callout — matches AstorThread composer
                  fontFamily: 'inherit',
                  '&::placeholder': { color: 'text.secondary' },
                  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
                }}
              />
              <GlassIconButton
                size="small"
                onClick={() => void handleSendMessage()}
                disabled={!inputValue.trim() || isStreaming}
                sx={{
                  backgroundColor: inputValue.trim() && !isStreaming ? 'primary.main' : 'transparent',
                  color: inputValue.trim() && !isStreaming ? 'white' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: inputValue.trim() && !isStreaming ? 'primary.dark' : 'transparent',
                  },
                  '&.Mui-disabled': { color: 'text.disabled' },
                }}
              >
                {isStreaming ? (
                  <ShimmerLoader variant="circular" width={20} height={20} />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </GlassIconButton>
            </Box>

            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
            >
              {t('astorChat.disclaimer')}{' '}
              <Typography
                component="span"
                variant="caption"
                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('astorChat.learnMore')}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default AstorChatFlyover;
